"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import "react-quill/dist/quill.snow.css";
import { cn } from "@/lib/utils";

// react-quill touches `document`, so it must be client-only.
const ReactQuill = dynamic(() => import("react-quill"), {
  ssr: false,
  loading: () => (
    <div className="h-40 animate-pulse rounded-xl bg-slate-100 dark:bg-zinc-800" />
  )
});

/**
 * Minimal toolbar — just the essentials for short-form rich text
 * (descriptions, notes). Intentionally NOT the full kitchen-sink toolbar.
 */
const MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"]
  ]
};

const FORMATS = ["bold", "italic", "underline", "list", "bullet", "link"];

export interface RichTextEditorV2Props {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Min height of the editing area in px (default 220). */
  minHeight?: number;
  className?: string;
}

/**
 * v2 rich-text editor: a minimal, design-language-aligned wrapper around
 * react-quill's "snow" theme. Visual styling lives in `src/styles/global.css`
 * under the `.rte-v2` scope (the quill classes are global).
 */
export function RichTextEditorV2({
  value = "",
  onChange,
  placeholder = "Escribe algo…",
  disabled = false,
  minHeight = 220,
  className
}: RichTextEditorV2Props) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  if (!mounted) {
    return (
      <div
        className="animate-pulse rounded-xl bg-slate-100 dark:bg-zinc-800"
        style={{ height: minHeight + 48 }}
      />
    );
  }

  return (
    <div
      className={cn("rte-v2", disabled && "rte-v2--disabled", className)}
      style={{ ["--rte-min-h" as string]: `${minHeight}px` } as React.CSSProperties}
    >
      <ReactQuill
        theme="snow"
        value={value}
        onChange={(html) => onChange?.(html)}
        modules={MODULES}
        formats={FORMATS}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </div>
  );
}
